import React, { useState } from 'react';
import PropTypes from 'prop-types';
import AuthCard, { FingerprintMark, PlayMark } from '../auth-card';
import AuthService from '~/modules/services/auth-service';
import PasskeyService, { REAUTHENTICATION_REQUIRED } from '~/modules/services/passkey-service';

/**
 * The screen that insists on a passkey.
 *
 * Shown at the two moments an account gains a session without having one: finishing an
 * invitation, and spending an emailed sign-in link. There is no way past it on purpose -- the
 * whole point of the passwordless flow is that the *next* sign-in has something better than a
 * mailbox behind it, and an offer everybody can decline is an offer nobody takes.
 *
 * The session is already minted by the time this renders, but the API scopes its token to the
 * passkey endpoints until one exists, so this screen and the token agree. Somebody who closes
 * the tab here holds a token that can register a passkey and nothing else, and is asked again
 * next time.
 */
const PasskeyEnrollment = ({ password, onDone }) => {
	const [enrolling, setEnrolling] = useState(false);
	const [error, setError] = useState(null);
	// The one just made, when it was device-bound. Windows Hello and security keys die with the
	// machine they were made on, so an account holding only that is one lost laptop from an
	// administrator reset -- and this screen is the last moment anybody is reliably willing to
	// make a second. A synced passkey is its own backup and is not asked about.
	const [needsBackup, setNeedsBackup] = useState(false);
	// The grant a sign-in link leaves lasts fifteen minutes, and this screen can be left open for
	// longer than that. When the API says the grant is gone there is nothing this screen can ask
	// for -- no password exists -- so the way forward is a fresh sign-in, offered rather than
	// leaving a button that can only fail.
	const [needsFreshSignIn, setNeedsFreshSignIn] = useState(false);

	const supported = PasskeyService.isSupported();

	// `on` names the device to make it on -- undefined for this one, `phone` for the backup.
	// Callers wrap it rather than passing `enrol` straight to `onClick`, which would hand it a
	// click event that reads as truthy and skip the backup step.
	const enrol = async(on) => {
		if( enrolling ) {
			return;
		}

		setEnrolling(true);
		setError(null);

		try {
			// The password is passed when we have just watched somebody type it, and left out
			// after a sign-in link -- the API grants that ceremony on the strength of the link
			// rather than asking for a password nobody used.
			const created = await PasskeyService.register({
				...(password ? { password } : {}),
				...(on ? { on } : {}),
				// The backup is the account's *second* passkey. The grant was spent making the
				// first, and an account holding one is asked to prove it with that one before
				// another is made -- so the service runs the confirmation ceremony first.
				...(needsBackup ? { confirmWith: 'passkey' } : {})
			});

			if( !on && created && !created.synced ) {
				setNeedsBackup(true);
				setEnrolling(false);

				return;
			}

			onDone();
		}
		catch(e) {
			// A dismissed prompt is not a failure worth a message, but it is worth staying here
			// for: the person still has no passkey.
			if( e?.message === REAUTHENTICATION_REQUIRED ) {
				setNeedsFreshSignIn(true);
			}
			else if( e?.name !== 'NotAllowedError' && e?.name !== 'AbortError' ) {
				setError(e?.message || 'That passkey could not be created');
			}

			setEnrolling(false);
		}
	};

	/**
	 * Nothing left to prove with on this screen: the sign-in that vouched for this person has
	 * aged out. A fresh one brings the grant back, and this screen with it.
	 */
	if( needsFreshSignIn ) {
		return (
			<AuthCard
				title="Sign in again"
				subtitle={'It has been a while since you signed in, so Pixwel needs to see you '
					+ 'again before a passkey can be added. Sign in and you will be brought '
					+ 'straight back here.'}
				mark={<FingerprintMark/>}
			>
				<button
					type="button"
					className="px-auth-card-submit"
					data-cy="passkey-enrollment-reauth"
					onClick={() => AuthService.logout()}
				>
					Sign in again
					<PlayMark/>
				</button>
			</AuthCard>
		);
	}

	/**
	 * A browser that cannot do this cannot get past here, and the honest thing is to say so.
	 *
	 * Rare -- every current browser can -- but "Continue" used to reload into the same gate:
	 * the session still owes a passkey and the API still scopes its token to enrolling one, so
	 * the only way forward is a browser that can hold a passkey. Signing out is the way to get
	 * there without leaving a half-usable session behind.
	 */
	if( !supported ) {
		return (
			<AuthCard
				title="Use a browser that supports passkeys"
				subtitle={'This browser cannot create a passkey, and this account signs in with '
					+ 'one. Sign out here and sign in again from a current browser -- Chrome, '
					+ 'Edge, Safari or Firefox -- to set it up.'}
			>
				<button
					type="button"
					className="px-auth-card-submit"
					data-cy="passkey-enrollment-unsupported"
					onClick={() => AuthService.logout()}
				>
					Sign out
					<PlayMark/>
				</button>
			</AuthCard>
		);
	}

	/**
	 * The second one, somewhere else.
	 *
	 * Offered rather than forced: somebody with a security key and no phone to hand would
	 * otherwise be held at a wall on the way into the app. It defaults to the cross-device
	 * ceremony, because the machine they are on has just refused to hold two.
	 */
	if( needsBackup ) {
		return (
			<AuthCard
				title="Add a backup"
				subtitle={'That passkey lives on this device only. If you lose it, you lose the '
					+ 'account with it -- so add a second one on a security key, a phone or a '
					+ 'tablet before you carry on.'}
				mark={<FingerprintMark/>}
			>
				{ error
					? <div className="px-auth-modal-error" data-cy="passkey-enrollment-error">{error}</div>
					: null }

				<button
					type="button"
					className="px-auth-card-submit"
					disabled={enrolling}
					data-cy="passkey-enrollment-backup"
					onClick={() => enrol('phone')}
				>
					{ enrolling ? 'Waiting for your device…' : 'Add a backup' }
					<PlayMark/>
				</button>

				<button
					type="button"
					className="px-auth-card-alternative"
					disabled={enrolling}
					data-cy="passkey-enrollment-backup-skip"
					onClick={onDone}
				>
					Not now
				</button>
			</AuthCard>
		);
	}

	return (
		<AuthCard
			title="Set up a passkey"
			subtitle={'A passkey signs you in with your fingerprint, face or screen lock -- '
				+ 'nothing to remember, and nothing worth stealing from us.'}
			mark={<FingerprintMark/>}
		>
			{ error
				? <div className="px-auth-modal-error" data-cy="passkey-enrollment-error">{error}</div>
				: null }

			<button
				type="button"
				className="px-auth-card-submit"
				disabled={enrolling}
				data-cy="passkey-enrollment-create"
				onClick={() => enrol()}
			>
				{ enrolling ? 'Waiting for your device…' : 'Create a passkey' }
				<PlayMark/>
			</button>
		</AuthCard>
	);
};

PasskeyEnrollment.propTypes = {
	// Only when this screen follows a form that collected one; absent after a sign-in link.
	password: PropTypes.string,
	onDone: PropTypes.func.isRequired
};

export default PasskeyEnrollment;
